import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Skeleton,
  Typography,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  useTheme,
  alpha,
  Stack,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import SecurityIcon from "@mui/icons-material/Security";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import { useSeguro } from "../../store/seguroViagem";
import { formatMoneyInString } from "../../util/helpers";
import { useMutation } from "react-query";
import {
  getCoberturas,
  addSeguroViagem,
} from "../../api/Carrinho/SeguroViagem";
import useAlert from "../../hooks/useAlert";
import ModalSeguroDetalhes from "../ModalSeguroDetalhes";
import { useNavigate } from "react-router-dom";

export default function SeguroDetalhesPacote({
  isValorVisible = false,
  showModal,
  handlerClose,
  setShowModal,
}) {
  const { infoSeguro, updateSelectPacote } = useSeguro();
  const { showAlertError, showAlertSuccess } = useAlert();
  const theme = useTheme();
  const navigate = useNavigate();
  const [detalhes, setDetalhesSeguros] = useState([]);
  const [categorias, setCategorias] = useState({});
  const [isLoadingContratar, setIsLoadingContratar] = useState(false);

  const mutationGetCobertura = useMutation(getCoberturas, {
    onSuccess: (response) => {
      if (response?.errorMessage && response?.errorMessage?.length > 0) {
        showAlertError(`Ahh, algo deu Errado: ${response.errorMessage}`);
        return;
      }

      const dados = response.data.data || [];
      setDetalhesSeguros(dados);

      // Agrupar coberturas por categoria
      const categoriasAgrupadas = dados.reduce((acc, item) => {
        const categoria = item.categoria || "Outras Coberturas";
        if (!acc[categoria]) {
          acc[categoria] = [];
        }
        acc[categoria].push(item);
        return acc;
      }, {});

      setCategorias(categoriasAgrupadas);
    },
    onError: async (err, variables, context) => {
      showAlertError(
        "Ahh, Algo deu errado, por favor informe ao suporte tecnico o seguinte erro - " +
          err
      );
    },
  });

  useEffect(() => {
    if (!Boolean(!!infoSeguro?.pacoteSelecionado?.nomeProduto)) {
      console.error("Não foi encontrado nenhum pacote");
      return;
    }

    if (showModal) {
      mutationGetCobertura.mutate(infoSeguro);
    }
  }, [showModal]);

  const getCategoriaIcon = (categoria) => {
    const icons = {
      "Assistência Médica": "🏥",
      Bagagem: "🎒",
      Cancelamento: "❌",
      "Acidentes Pessoais": "⚠️",
      "Outras Coberturas": "🛡️",
    };
    return icons[categoria] || "✓";
  };

  const isCoberturaIncluida = (valorCobertura) => {
    return (
      valorCobertura?.toUpperCase() === "INCLUIDO" ||
      valorCobertura?.toUpperCase() === "SIM" ||
      !valorCobertura
    );
  };

  const handlerContratarSeguro = async () => {
    if (!infoSeguro.pacoteSelecionado) {
      showAlertError("Ops, é necessario escolher um pacote");
      return;
    }

    setIsLoadingContratar(true);

    try {
      const response = await addSeguroViagem(
        infoSeguro,
        infoSeguro.pacoteSelecionado
      );
      updateSelectPacote(infoSeguro.pacoteSelecionado);

      if (!!response?.errorMessage) {
        showAlertError(
          `Ops, algo deu errado: ${response.errorMessage} - R06JF`
        );
        return;
      }

      showAlertSuccess("Seguro adicionado com sucesso! Redirecionando...");

      // Fecha o modal e redireciona após um breve delay
      setTimeout(() => {
        setShowModal(false);
        navigate("/SeguroViagem/Passageiros");
      }, 1500);
    } catch (error) {
      showAlertError(`Erro ao contratar seguro: ${error.message}`);
    } finally {
      setIsLoadingContratar(false);
    }
  };

  return (
    <ModalSeguroDetalhes
      open={showModal}
      onClose={handlerClose()}
      maxWidth="lg"
    >
      {/* Header do Modal */}
      <Card
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          borderRadius: 0,
          position: "relative",
        }}
      >
        <CardContent sx={{ p: 3, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1, pr: 2 }}>
              <Chip
                label="Detalhes do Seguro"
                sx={{
                  bgcolor: "white",
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                  mb: 2,
                }}
                size="small"
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                }}
              >
                {infoSeguro.pacoteSelecionado?.nomeProduto || "Seguro Viagem"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Confira todas as coberturas e benefícios incluídos
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Conteúdo do Modal */}
      <Box
        sx={{
          p: 3,
          overflowY: "auto",
          flex: 1,
          maxHeight: { xs: "50vh", md: "45vh" },
        }}
      >
        {mutationGetCobertura.isLoading ? (
          // Skeleton Loading
          <Grid container spacing={3}>
            {Array.from({ length: 4 }, (_, index) => (
              <Grid item xs={12} key={index}>
                <Skeleton height={40} animation="wave" width="60%" />
                {Array.from({ length: 3 }, (_, subIndex) => (
                  <Box
                    key={subIndex}
                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                  >
                    <Skeleton
                      variant="circular"
                      width={20}
                      height={20}
                      sx={{ mr: 2 }}
                    />
                    <Skeleton height={20} animation="wave" width="80%" />
                  </Box>
                ))}
              </Grid>
            ))}
          </Grid>
        ) : Object.keys(categorias).length > 0 ? (
          // Lista de Coberturas Agrupadas
          <Grid container spacing={3}>
            {Object.entries(categorias).map(([categoria, itens], index) => (
              <Grid item xs={12} md={12} key={categoria}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: 2,
                      borderColor: theme.palette.primary.light,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          borderRadius: 1,
                          p: 1,
                          mr: 2,
                        }}
                      >
                        <Typography sx={{ fontSize: 20 }}>
                          {getCategoriaIcon(categoria)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          color: theme.palette.primary.main,
                          fontSize: "1.1rem",
                        }}
                      >
                        {categoria}
                      </Typography>
                    </Box>

                    <List dense sx={{ py: 0 }}>
                      {itens.map((item, itemIndex) => (
                        <ListItem
                          key={itemIndex}
                          sx={{
                            px: 0,
                            py: 0.75,
                            borderBottom:
                              itemIndex < itens.length - 1
                                ? `1px solid ${alpha(
                                    theme.palette.divider,
                                    0.5
                                  )}`
                                : "none",
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon
                              sx={{
                                fontSize: 16,
                                color: isCoberturaIncluida(item.valorCobertura)
                                  ? theme.palette.success.main
                                  : theme.palette.text.secondary,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, lineHeight: 1.4 }}
                              >
                                {item.detalhe}
                              </Typography>
                            }
                            secondary={
                              item.valorCobertura &&
                              !isCoberturaIncluida(item.valorCobertura) && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    fontStyle: "italic",
                                    display: "block",
                                    mt: 0.5,
                                  }}
                                >
                                  {item.valorCobertura}
                                </Typography>
                              )
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          // Estado vazio
          <Box sx={{ textAlign: "center", py: 4 }}>
            <SecurityIcon
              sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              Nenhuma cobertura encontrada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              As informações de cobertura não estão disponíveis no momento.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer com Valor e Botões de Ação */}
      <Box
        sx={{
          p: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          {/* Informações do Valor */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <PriceCheckIcon
                sx={{
                  color: theme.palette.success.main,
                  mr: 1,
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Valor do Seguro
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Inclui todas as coberturas listadas
            </Typography>
            {isValorVisible && infoSeguro.pacoteSelecionado?.valorTotal && (
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                }}
              >
                {formatMoneyInString(infoSeguro.pacoteSelecionado.valorTotal)}
              </Typography>
            )}
          </Grid>

          {/* Botões de Ação */}
          <Grid item xs={12} md={6}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="outlined"
                onClick={handlerClose()}
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: "bold",
                  minWidth: { xs: "100%", sm: 120 },
                }}
              >
                Fechar
              </Button>

              <Button
                variant="contained"
                onClick={handlerContratarSeguro}
                disabled={isLoadingContratar || !infoSeguro.pacoteSelecionado}
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: "bold",
                  minWidth: { xs: "100%", sm: 160 },
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                  },
                }}
              >
                {isLoadingContratar ? (
                  <>
                    <Box
                      component="span"
                      sx={{
                        width: 16,
                        height: 16,
                        border: `2px solid transparent`,
                        borderTop: `2px solid white`,
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        mr: 1,
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                    Contratando...
                  </>
                ) : (
                  "Contratar Agora"
                )}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </ModalSeguroDetalhes>
  );
}
